import { useCallback, useEffect, useState } from 'react'
import Boton from '../components/Boton'
import { emailsApi } from '../api/emails'
import Paginador from '../components/Paginador'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFechaHora } from '../utils/fecha'
import { descargarArchivo } from '../utils/descargar'
import { colors, sx, text, space, radius } from '../design/tokens'

// Estado del mail → color + etiqueta. "enviado" = LGR se lo entregó al
// proveedor (Postmark/Brevo) y todavía no llegó su confirmación.
const ESTADOS = {
  enviado:   { label: 'Enviado',   bg: colors.infoBg, fg: colors.info },
  entregado: { label: 'Entregado', bg: colors.okBg,   fg: colors.ok },
  rebotado:  { label: 'Rebotado',  bg: colors.errBg,  fg: colors.err },
  spam:      { label: 'Spam',      bg: colors.errBg,  fg: colors.err },
  bloqueado: { label: 'Bloqueado', bg: colors.errBg,  fg: colors.err },
  fallido:   { label: 'Falló',     bg: colors.errBg,  fg: colors.err },
}

// Slug del mailable → nombre humano.
const TIPOS = {
  pedido_web:          'Confirmación de pedido',
  comprobantes:        'Comprobantes',
  verificacion_cuenta: 'Verificación de cuenta',
  gift_card:           'Gift card',
  pago_cancelado:      'Cobro cancelado',
  presupuesto:         'Presupuesto',
  portal_cliente:      'Acceso al portal',
  seguimiento:         'Seguimiento del envío',
}

// Por qué proveedor salió (lo graba el backend con el mailer real del envío).
const PROVEEDORES = {
  postmark: { letra: 'P', nombre: 'Postmark', bg: colors.okBg, fg: colors.ok },
  brevo:    { letra: 'B', nombre: 'Brevo',    bg: colors.infoBg, fg: colors.info },
  ses:      { letra: 'S', nombre: 'Amazon SES', bg: colors.bgSoft, fg: colors.textSec },
}

function ProveedorBadge({ proveedor }) {
  if (!proveedor) return <span style={{ color: colors.textMeta }} title="Sin dato del proveedor">—</span>
  const p = PROVEEDORES[proveedor] || { letra: proveedor.slice(0, 1).toUpperCase(), nombre: proveedor, bg: colors.bgSoft, fg: colors.textSec }
  return <span style={{ ...sx.badge, background: p.bg, color: p.fg, fontWeight: 700 }} title={`Enviado por ${p.nombre}`}>{p.letra}</span>
}

function EstadoBadge({ estado }) {
  const e = ESTADOS[estado] || { label: estado || '—', bg: colors.bgSoft, fg: colors.textMeta }
  return <span style={{ ...sx.badge, background: e.bg, color: e.fg }}>{e.label}</span>
}

export default function CorreosPage() {
  const [rows, setRows] = useState([])
  const [resumen, setResumen] = useState(null)
  const [loading, setLoading] = useState(true)
  const [q, setQBase] = useState('')
  const [estado, setEstadoBase] = useState('')
  const [detalle, setDetalle] = useState(null)
  const [pagina, setPagina] = useState(1)
  const [paginacion, setPaginacion] = useState({ last_page: 1, total: 0 })
  // Cambiar el filtro o la búsqueda vuelve a la primera hoja.
  const setQ = (v) => { setQBase(v); setPagina(1) }
  const setEstado = (v) => { setEstadoBase(v); setPagina(1) }

  // El listado no trae el cuerpo (pesa); se pide al abrir el detalle.
  async function abrirDetalle(row) {
    setDetalle(row)
    try {
      const { data } = await emailsApi.ver(row.id)
      setDetalle(data)
    } catch { /* queda el resumen que ya teníamos */ }
  }

  const cargar = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true)
    try {
      const params = { page: pagina }
      if (q.trim()) params.q = q.trim()
      if (estado) params.estado = estado
      const [lista, res] = await Promise.all([
        emailsApi.listar(params),
        emailsApi.resumen({}),
      ])
      setRows(lista.data?.data || [])
      setPaginacion({ last_page: lista.data?.last_page || 1, total: lista.data?.total || 0 })
      setResumen(res.data)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [q, estado, pagina])

  useEffect(() => {
    const t = setTimeout(() => cargar(), 250)   // debounce del buscador
    return () => clearTimeout(t)
  }, [cargar])

  useAutoRefresh(() => cargar({ silent: true }), 30000)

  const problemas = resumen?.con_problema || 0

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.lg, marginBottom: space.xl }}>
        <h2 style={{ margin: 0, fontSize: 20, color: colors.text }}>Correos</h2>
        <span style={{ fontSize: text.sec, color: colors.textSec }}>
          Todo mail que sale de LGR y qué pasó con él
        </span>
      </div>

      {problemas > 0 && (
        <div style={{
          background: colors.errBg, color: colors.err, padding: `${space.lg}px ${space.xl}px`,
          borderRadius: radius.md, marginBottom: space.xl, fontSize: text.body,
        }}>
          <strong>{problemas}</strong> {problemas === 1 ? 'correo no llegó' : 'correos no llegaron'} a destino
          (rebote, spam o bloqueo). Filtralos abajo para ver a quiénes.
        </div>
      )}

      {/* Chips por estado */}
      <div style={{ display: 'flex', gap: space.md, marginBottom: space.lg, flexWrap: 'wrap' }}>
        <Chip activo={estado === ''} onClick={() => setEstado('')}>
          Todos {resumen ? `(${Object.values(resumen.por_estado || {}).reduce((a, b) => a + Number(b), 0)})` : ''}
        </Chip>
        {Object.entries(ESTADOS).map(([k, e]) => {
          const n = Number(resumen?.por_estado?.[k] || 0)
          if (!n) return null
          return (
            <Chip key={k} activo={estado === k} onClick={() => setEstado(estado === k ? '' : k)}
              bg={e.bg} fg={e.fg}>
              {e.label} ({n})
            </Chip>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: space.md, marginBottom: space.lg }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por email o asunto…"
          style={{
            flex: 1, maxWidth: 380, padding: '8px 10px', fontSize: text.body,
            border: `1px solid ${colors.border}`, borderRadius: 6, color: colors.text,
          }}
        />
        <Boton variante="ghost" sm onClick={() => cargar()}>↻ Actualizar</Boton>
      </div>

      {loading ? (
        <p style={{ color: colors.textSec }}>Cargando…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: colors.textSec }}>
          {q || estado ? 'No hay correos que coincidan con el filtro.' : 'Todavía no salió ningún correo.'}
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              {['Fecha', 'Tipo', 'Destinatario', 'Asunto', 'Vía', 'Estado'].map((h) => (
                <th key={h} style={{ ...sx.th, textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => abrirDetalle(r)}
                style={{ borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgSoft }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '' }}
              >
                <td style={{ padding: '10px 8px', ...sx.mono, color: colors.textSec, whiteSpace: 'nowrap' }}>
                  {fmtFechaHora(r.created_at)}
                </td>
                <td style={{ padding: '10px 8px', color: colors.text }}>{TIPOS[r.tipo] || r.tipo || '—'}</td>
                <td style={{ padding: '10px 8px', ...sx.mono, color: colors.text }}>{r.destino}</td>
                <td style={{ padding: '10px 8px', color: colors.textSec, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.asunto}
                </td>
                <td style={{ padding: '10px 8px' }}><ProveedorBadge proveedor={r.proveedor} /></td>
                <td style={{ padding: '10px 8px' }}><EstadoBadge estado={r.estado} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && (
        <Paginador pagina={pagina} totalPaginas={paginacion.last_page} onCambiar={setPagina}
          info={`${paginacion.total.toLocaleString('es-AR')} correos`} />
      )}

      {detalle && <ModalDetalle mail={detalle} onClose={() => setDetalle(null)} />}
    </div>
  )
}

function Chip({ activo, onClick, children, bg, fg }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 12px', fontSize: text.sec, borderRadius: 999, cursor: 'pointer',
        border: `1px solid ${activo ? (fg || colors.link) : colors.border}`,
        background: activo ? (bg || colors.linkBg) : colors.bg,
        color: activo ? (fg || colors.link) : colors.textSec,
        fontWeight: activo ? 600 : 400,
      }}
    >{children}</button>
  )
}

function ModalDetalle({ mail, onClose }) {
  // 'info' = qué pasó con el mail · 'mail' = el mail tal cual lo recibió el cliente
  const [vista, setVista] = useState('info')

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: colors.overlay, display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: space.xl,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bg, borderRadius: 10, padding: space['3xl'],
          maxWidth: vista === 'mail' ? 780 : 620, width: '100%', maxHeight: '85vh', overflow: 'auto',
        }}
      >
        <h3 style={{ margin: `0 0 ${space.lg}px`, fontSize: text.title, color: colors.text }}>
          {TIPOS[mail.tipo] || mail.tipo || 'Correo'}
        </h3>

        {/* Solapas: el informe de entrega / el mail tal cual salió */}
        <div style={{ display: 'flex', gap: space.md, marginBottom: space.xl, borderBottom: `1px solid ${colors.border}` }}>
          {[['info', 'Qué pasó'], ['mail', 'Ver el mail']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setVista(k)}
              style={{
                padding: '6px 2px', marginBottom: -1, background: 'none', cursor: 'pointer',
                border: 'none', borderBottom: `2px solid ${vista === k ? colors.link : 'transparent'}`,
                color: vista === k ? colors.link : colors.textSec,
                fontSize: text.body, fontWeight: vista === k ? 600 : 400,
              }}
            >{label}</button>
          ))}
        </div>

        {vista === 'mail' ? (
          <VistaMail mail={mail} />
        ) : (
        <>
        <Dato k="Para" v={mail.destino} mono />
        <Dato k="Asunto" v={mail.asunto} />
        <Dato k="Estado" v={<EstadoBadge estado={mail.estado} />} />
        <Dato k="Enviado por" v={PROVEEDORES[mail.proveedor]?.nombre || mail.proveedor || '—'} />
        {mail.error && <Dato k="Motivo" v={mail.error} />}
        <Dato k="Enviado" v={fmtFechaHora(mail.enviado_at || mail.created_at)} mono />
        {mail.entregado_at && <Dato k="Entregado" v={fmtFechaHora(mail.entregado_at)} mono />}
        {mail.abierto_at && <Dato k="Abierto" v={fmtFechaHora(mail.abierto_at)} mono />}
        {mail.cliente && <Dato k="Cliente" v={mail.cliente.nombre} />}

        {Array.isArray(mail.eventos) && mail.eventos.length > 0 && (
          <>
            <p style={{
              marginTop: space['2xl'], marginBottom: space.md, fontSize: text.meta,
              textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, fontWeight: 600,
            }}>Eventos de {PROVEEDORES[mail.proveedor]?.nombre || 'entrega'}</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: text.sec, color: colors.textSec }}>
              {mail.eventos.map((e, i) => (
                <li key={i}>{e.at} — <strong>{e.evento}</strong>{e.datos?.reason ? ` · ${e.datos.reason}` : ''}</li>
              ))}
            </ul>
          </>
        )}
        </>
        )}

        <div style={{ marginTop: space['2xl'], textAlign: 'right' }}>
          <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
        </div>
      </div>
    </div>
  )
}

/**
 * El mail tal cual salió. Va en un iframe con `sandbox` vacío: sin scripts, sin
 * formularios, sin navegación — el HTML del mail no puede tocar la app.
 */
function VistaMail({ mail }) {
  if (mail.html === undefined) {
    return <p style={{ color: colors.textSec, fontSize: text.body }}>Cargando el mail…</p>
  }
  if (!mail.html) {
    return (
      <p style={{ color: colors.textSec, fontSize: text.body }}>
        Este correo no quedó guardado (salió antes de que se empezaran a archivar).
      </p>
    )
  }

  return (
    <>
      <div style={{ fontSize: text.sec, color: colors.textSec, marginBottom: space.md }}>
        <strong style={{ color: colors.text }}>{mail.asunto}</strong><br />
        Para {mail.destino} · {fmtFechaHora(mail.enviado_at || mail.created_at)}
      </div>
      {Array.isArray(mail.adjuntos) && mail.adjuntos.length > 0 && (
        <div style={{ fontSize: text.sec, color: colors.textSec, marginBottom: space.md, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span>📎</span>
          {mail.adjuntos.map((a, i) => {
            // Formato nuevo: {nombre, path, bytes} (el PDF quedó archivado y se
            // puede bajar). Filas viejas: solo el nombre (string), sin archivo.
            const nombre = typeof a === 'string' ? a : a.nombre
            const descargable = typeof a === 'object' && a?.path
            return descargable ? (
              <button
                key={i}
                onClick={() => descargarArchivo(`/emails/${mail.id}/adjuntos/${i}`, nombre).catch(() => window.alert('No se pudo descargar el adjunto.'))}
                style={{
                  border: `1px solid ${colors.border}`, background: '#fff', borderRadius: radius.sm,
                  padding: '3px 10px', fontSize: text.sec, color: colors.primary, cursor: 'pointer',
                }}
                title="Descargar el PDF tal cual se envió"
              >
                ⬇ {nombre}
              </button>
            ) : (
              <span key={i} title="Este adjunto salió antes de que se empezaran a archivar">{nombre}</span>
            )
          })}
        </div>
      )}
      <iframe
        title="Contenido del correo"
        srcDoc={mail.html}
        sandbox=""
        style={{
          width: '100%', height: '55vh', border: `1px solid ${colors.border}`,
          borderRadius: radius.sm, background: '#fff',
        }}
      />
    </>
  )
}

function Dato({ k, v, mono }) {
  return (
    <div style={{ display: 'flex', gap: space.lg, padding: '5px 0', fontSize: text.body }}>
      <span style={{ color: colors.textSec, minWidth: 90 }}>{k}</span>
      <span style={{ color: colors.text, ...(mono ? sx.mono : {}) }}>{v}</span>
    </div>
  )
}
